/**
 * @file AgencyList.js
 * @description This page displays a list of agencies with their respective details such as name, code, etc...
 */
import '../page-list/PageList.css';
import styles from './AgencyList.module.scss';
import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import {
    FilterModal,
    useFilter,
    renderFilterIcon,
} from '../helpers/FilterComponent';
import { NavLink } from 'react-router-dom';
import {
    deleteAgencies,
    updateAgency,
    getAgency,
    getAgencies,
} from '../../services/systemService';
import PaginationComponent from '../helpers/PaginationComponent';
import { LogEventType, SecurityAttributes } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { ROUTE_BUILDERS } from '../../constants/routes';
const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};
import { useAuthContext } from '../../store/Contexts';
import { getText } from '../../i18n';

const AgencyList = () => {
    const navigate = useNavigate();
    const [agencies, setAgencies] = useState([]);
    const { showModal } = useContext(ModalContext);
    const [isLoading, setIsLoading] = useState(true);
    const [rowsClicked, setRowsClicked] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: SortDirections.ASCENDING,
    });
    const [selectedReseller, setSelectedReseller] = useState(null);
    const [filteredData, applyFilter] = useFilter(agencies ?? []);
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const [totalPages, setTotalPages] = useState(2);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const { hasAttribute } = useAuthContext();

    const canEditForm = hasAttribute(SecurityAttributes.AgencyFullAccess);

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleApply = (column, selectedOptions) => {
        if (column === 'reseller') {
            const newResellerIds =
                selectedOptions.includes('Yes') &&
                    selectedOptions.includes('No')
                    ? null
                    : selectedOptions.includes('Yes')
                        ? true
                        : selectedOptions.includes('No')
                            ? false
                            : null;

            setSelectedReseller(newResellerIds);
            return;
        }
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        applyFilter(column, selectedOptions);
    };

    // Compute available options considering active filters for current modal
    const getOptions = column => {
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column];

        const filteredData = agencies.filter(agency =>
            Object.entries(filtersWithoutCurrent).every(
                ([key, values]) =>
                    values.length === 0 || values.includes(agency[key]),
            ),
        );

        return Array.from(new Set(filteredData.map(item => item[column])))
            .filter(Boolean) // Remove null/undefined values
            .sort((a, b) => {
                if (typeof a === 'string' && typeof b === 'string') {
                    return a.localeCompare(b);
                }
                return a - b;
            });
    };

    useEffect(() => {
        loadAgencies();
    }, [currentPage, sortConfig, selectedReseller]);

    const loadAgencies = async () => {
        try {
            const fetchedAgencies = await getAgencies(
                currentPage,
                pageSize,
                sortConfig.key,
                sortConfig.direction,
                selectedReseller,
            );
            setAgencies(fetchedAgencies.viewModels);
            setTotalPages(fetchedAgencies.pageTotal);
        } catch (error) {
            if (error.name === 'AbortError') {
                return null;
            }
            console.error('Failed to get agencies', error);
            Toast.error(getText('msg_27ded7882fa5'));
        } finally {
            setIsLoading(false);
        }
    };

    let handleSortName = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'name',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'name',
                direction: SortDirections.ASCENDING,
            });
        }
    };

    let handleSortAgencyCode = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'code',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'code',
                direction: SortDirections.ASCENDING,
            });
        }
    };

    let handleSortApplicationName = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'applicationName',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'applicationName',
                direction: SortDirections.ASCENDING,
            });
        }
    };

    let handleSortReseller = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'reseller',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'reseller',
                direction: SortDirections.ASCENDING,
            });
        }
    };

    const handlerRowClicked = agencyId => {
        setRowsClicked(prevSelection =>
            prevSelection.includes(agencyId)
                ? prevSelection.filter(id => id !== agencyId)
                : [...prevSelection, agencyId],
        );
    };

    const onToggle = async id => {
        try {
            var agency = await getAgency(id);

            if (!agency) throw new Error('Agency not found');

            const updatedAgency = { ...agency, isActive: !agency.isActive };

            if (agency.isActive) {
                showModal({
                    type: ModalTypes.ACTION,
                    subtitle: getText('msg_92c103804f84'),
                    text: getText('msg_120a42f56e3f'),
                    onYes: () => onUpdate(updatedAgency),
                    onNo: () => { },
                    noButtonText: getText('msg_77dfd2135f4d'),
                    yesButtonText: getText('msg_9a7d4e0687b1'),
                });

                return;
            }

            await updateAgency(updatedAgency);
            Toast.success(getText('msg_d546edbcd1bb'));
            loadAgencies();
        } catch (error) {
            console.error('Failed to toggle agency enabled status', error);
            Toast.error(getText('msg_97c8a6545314'));
        }
    };

    const onUpdate = async agency => {
        try {
            await updateAgency(agency);
            Toast.success(getText('msg_a24cc5403591'));
            loadAgencies();
        } catch (error) {
            console.error('Failed to toggle agency enabled status', error);
            Toast.error(getText('msg_97c8a6545314'));
        }
    };

    const onDeleteAgencies = async () => {
        try {
            await deleteAgencies(rowsClicked);
            Toast.success(
                `${rowsClicked.length > 1 ? 'Agencies' : 'Agency'} deleted successfully`,
            );
            loadAgencies();
            setRowsClicked([]);
        } catch (error) {
            console.error(
                `Failed to delete ${rowsClicked.length > 1 ? 'Agencies' : 'Agency'}`,
                error,
            );
            Toast.error(
                `There was an issue deleting ${rowsClicked.length > 1 ? 'Agencies' : 'Agency'}.`,
            );
        }
    };

    const rowAction = async action => {
        if (rowsClicked.length > 0) {
            switch (action) {
                case 'delete': {
                    let canBeDeleted = true;
                    for (let i = 0; i < rowsClicked.length; i++) {
                        const agencyId = rowsClicked[i];
                        const fetchedAgency = await getAgency(agencyId);
                        if (!(fetchedAgency?.canBeDeleted ?? true)) {
                            canBeDeleted = false;
                            break;
                        }
                    }
                    if (canBeDeleted) {
                        showModal({
                            type: ModalTypes.DELETE_CONFIRMATION,
                            title: getText('msg_b957efeb6067'),
                            text: `This action will delete ${rowsClicked.length > 1 ? 'all selected agencies' : 'this agency'}.`,
                            onYes: () => onDeleteAgencies(),
                            onNo: () => { },
                            noButtonText: getText('msg_77dfd2135f4d'),
                            yesButtonText: getText('msg_f6fdbe48dc54'),
                        });
                        logEvent(
                            LogEventType.DeleteClicked,
                            `Agency delete clicked`,
                        );
                    } else {
                        showModal({
                            type: ModalTypes.ACTION,
                            title: getText('msg_b78130c92f32'),
                            subtitle: `${rowsClicked.length > 1 ? 'One or more of these agencies are' : 'This agency is'} currently in use`,
                            text: getText('msg_98a77866cfbc'),
                            onYes: () => { },
                            onNo: () => { },
                            noButtonText: getText('msg_77dfd2135f4d'),
                            yesButtonText: getText('msg_2e02623966f9'),
                        });
                    }

                    break;
                }
                default: {
                    break;
                }
            }
        } else {
            Toast.error(
                getText('msg_9bcf67dc54d5'),
            );
        }
    };

    return isLoading ? (
        <Spinner width={50} className={styles.loadingSpinner} />
    ) : (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    {/* Screen Header with Title and Actions */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        {/* Screen Title */}
                        <div className="float-start d-flex align-items-center">
                            <p className="page-title card-title ms-4 me-5">
                                {getText('msg_7d7ed24ad5aa')}
                            </p>
                        </div>
                        {/* Screen Actions */}
                        {canEditForm && (
                            <div className="float-end d-flex align-items-center">
                                <span
                                    type="button"
                                    onClick={() => rowAction('delete')}
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-trash3 me-2" />
                                    {getText('msg_f6fdbe48dc54')}
                                </span>
                                <span
                                    onClick={() =>
                                        navigate(ROUTE_BUILDERS.agenciesView(-1))
                                    }
                                    type="button"
                                    className="ms-3 me-4  button-base ing-button-primary-left align-middle"
                                >
                                    <i className="bi bi-plus-lg me-1" />
                                    {getText('msg_61cc55aa0453')}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="table-responsive-xxl w-100">
                        {/* Template List Table */}
                        <table
                            className={`table table-hover align-middle ${styles.agencyTable}`}
                        >
                            <thead className="custom-table-header table-light">
                                <tr>
                                    {canEditForm && (
                                        <th
                                            className={`text-center ${styles.selectionColumn}`}
                                        >
                                            <svg
                                                width="19"
                                                height="19"
                                                viewBox="0 0 19 19"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M5.2 9.3H12.8"
                                                    stroke="#0070FF"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                                <rect
                                                    x="0.5"
                                                    y="0.5"
                                                    width="17"
                                                    height="17"
                                                    rx="4"
                                                    stroke="#0070FF"
                                                />
                                            </svg>
                                        </th>
                                    )}
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_515990ab0996')}
                                        </span>
                                        <span
                                            onClick={handleSortName}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'name' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={() => openModal('name')}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['name']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'name' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_adac69379a62')}
                                        </span>
                                        <span
                                            onClick={handleSortAgencyCode}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'code' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={() => openModal('code')}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['code']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'code' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th className={styles.applicationColumn}>
                                        <span className="table-column-header-text">
                                            {getText('msg_5c6b271ce0be')}
                                        </span>
                                        <span
                                            onClick={handleSortApplicationName}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'applicationName' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={() =>
                                                openModal('applicationName')
                                            }
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['applicationName']
                                                    ?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'applicationName' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th
                                        className={`text-center ${styles.resellerColumn}`}
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_a259ac911046')}
                                        </span>
                                        <span
                                            onClick={handleSortReseller}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'reseller' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={() =>
                                                openModal('reseller')
                                            }
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selectedReseller !== null,
                                            )}
                                        </span>
                                        {activeModal === 'reseller' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={['Yes', 'No']}
                                                selectedOptions={
                                                    selectedReseller !== null
                                                        ? [
                                                            selectedReseller
                                                                ? 'Yes'
                                                                : 'No',
                                                        ]
                                                        : []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th
                                        className={`text-center ${styles.enableColumn}`}
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_20063ad90532')}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.map(agency => (
                                    <tr
                                        key={agency.id}
                                        className={`${rowsClicked.includes(agency.id)
                                            ? 'clicked-class'
                                            : ''
                                            }`}
                                    >
                                        {canEditForm && (
                                            <td
                                                className={`text-center ${styles.selectionColumn}`}
                                            >
                                                <input
                                                    id={agency.id}
                                                    onClick={() =>
                                                        handlerRowClicked(agency.id)
                                                    }
                                                    className="form-check-input"
                                                    type="checkbox"
                                                />
                                            </td>)}
                                        <td>
                                            <NavLink
                                                to={ROUTE_BUILDERS.agenciesView(
                                                    agency.id,
                                                )}
                                                className="titleCell"
                                            >
                                                {agency.name}
                                            </NavLink>
                                        </td>
                                        <td className="subtitleCell">
                                            {agency.code}
                                        </td>
                                        <td
                                            className={`subtitleCell ${styles.applicationColumn}`}
                                        >
                                            {agency?.theme?.name}
                                        </td>
                                        <td
                                            className={`subtitleCell text-center ${styles.resellerColumn}`}
                                        >
                                            {agency?.isReseller ? 'Y' : '-'}
                                        </td>
                                        <td
                                            className={`text-center ${styles.enableColumn}`}
                                        >
                                            <div className={`form-switch`}>
                                                <input
                                                    onClick={() =>
                                                        onToggle(agency.id)
                                                    }
                                                    className="form-check-input custom-switch-input"
                                                    type="checkbox"
                                                    checked={
                                                        agency.isActive
                                                            ? true
                                                            : false
                                                    }
                                                    disabled={!canEditForm}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {(agencies == null || agencies.length < 1) && (
                            <NotFoundBox
                                backgroundColor="#f0f4ff"
                                title={getText('msg_239b7ac90310')}
                                description={getText('msg_74df03180584')}
                            />
                        )}
                        {totalPages > 1 && (
                            <div className="mt-auto">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
export default AgencyList;
